import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Tab, Nav, Table, Button, Badge, Form, Modal, Alert, Tabs } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faList, faUsers, faChartLine, faEdit, faTrash, faPlus, faCheckCircle, faTimesCircle, faTruck, faEye, faSignOutAlt, faCog, faBell, faShield, faStore, faCreditCard, faLifeRing, faUtensils } from '@fortawesome/free-solid-svg-icons';
import axios from "axios";
import { API, API_BASE } from '../config/api';
import getImageUrl from '../utils/getImageUrl';
import { FaUsers, FaBox, FaEdit, FaTrash } from "react-icons/fa";
import './pages.css';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showProductModal, setShowProductModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [editingPaymentStatus, setEditingPaymentStatus] = useState(null);
  const [orderDateFilter, setOrderDateFilter] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editData, setEditData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "customer"
  });
  const [dashboardStats, setDashboardStats] = useState({
    totalOrders: 0,
    revenue: 0,
    activeProducts: 0,
    recentOrders: []
  });
  const [settings, setSettings] = useState({
    store: {
      storeName: 'Noureesh Foods',
      storeEmail: 'contact@noureeshfoods.com',
      storePhone: '+234 123 456 7890',
      storeAddress: '123 Food Street, Lagos, Nigeria',
      currency: '₦',
      taxRate: 7.5,
      deliveryFee: 1500,
      minOrderAmount: 5000,
      openingTime: '08:00',
      closingTime: '22:00'
    },
    notifications: {
      emailNotifications: true,
      orderAlerts: true,
      lowStockAlerts: true,
      newUserAlerts: false,
      marketingEmails: false
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: 60,
      passwordExpiry: 90,
      loginAttempts: 5,
      ipWhitelist: []
    },
    payments: {
      cashOnDelivery: true,
      bankTransfer: true,
      cardPayment: false,
      paystack: false,
      flutterwave: false
    }
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [supportRequests, setSupportRequests] = useState([]);
  const [supportRequestsLoading, setSupportRequestsLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestStatusFilter, setRequestStatusFilter] = useState('');
  const [requestCategoryFilter, setRequestCategoryFilter] = useState('');
  const [responseText, setResponseText] = useState('');
  const [showZoneModal, setShowZoneModal] = useState(false);  
  const [currentZone, setCurrentZone] = useState(null);
  const [zoneFormData, setZoneFormData] = useState({
    name: '',
    areas: '',
    deliveryFee: 1500,
    minOrderAmount: 0,
    estimatedDeliveryTime: '30-45 minutes',
    isActive: true
  });
  // Fetch settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      if (activeTab === 'settings') {
        try {
          setSettingsLoading(true);
          const token = localStorage.getItem('token');
          const response = await fetch(`${API}/settings`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) throw new Error('Failed to fetch settings');
          
          const settingsData = await response.json();
          
          // Ensure deliveryZones is always an array and properly structured
          const formattedSettings = {
            ...settingsData,
            deliveryZones: Array.isArray(settingsData.store?.deliveryZones) ? 
              settingsData.store.deliveryZones : []
          };
          
          setSettings(formattedSettings);
        } catch (err) {
          console.error('Error fetching settings:', err);
          // Set empty delivery zones on error
          setSettings(prev => ({
            ...prev,
            deliveryZones: []
          }));
        } finally {
          setSettingsLoading(false);
        }
      }
    };

    fetchSettings();
  }, [activeTab]);

  useEffect(() => {
    const checkAdminAuth = () => {
      const isAdminAuthenticated = localStorage.getItem('isAdminAuthenticated') === 'true';
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      
      if (!isAdminAuthenticated || adminUser.role !== 'admin') {
        setError('Access denied. Admin privileges required.');
        navigate('/admin-login');
      }
    };
    
    checkAdminAuth();
  }, [navigate]);

  // Auto-dismiss alerts after 4 seconds
  useEffect(() => {
    let successTimer;
    let errorTimer;

    if (successMessage) {
      successTimer = setTimeout(() => {
        setSuccessMessage('');
      }, 4000);
    }

    if (error) {
      errorTimer = setTimeout(() => {
        setError(null);
      }, 4000);
    }

    // Cleanup timers on component unmount or when messages change
    return () => {
      clearTimeout(successTimer);
      clearTimeout(errorTimer);
    };
  }, [successMessage, error]);

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setIsLoading(true);
        const [ordersRes, productsRes] = await Promise.all([
          fetch(`${API}/orders`),
          fetch(`${API}/products`)
        ]);

        if (!ordersRes.ok) throw new Error('Failed to fetch orders');
        if (!productsRes.ok) throw new Error('Failed to fetch products');

        const ordersData = await ordersRes.json();
        const productsData = await productsRes.json();
        
        // Calculate total revenue
        const revenue = ordersData.reduce((total, order) => total + order.total, 0);
        
        // Get recent orders (last 5)
        const recentOrders = ordersData
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5);
        
        setDashboardStats({
          totalOrders: ordersData.length,
          revenue,
          activeProducts: productsData.length,
          recentOrders
        });
        
        setOrders(ordersData);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (activeTab === 'dashboard') {
      fetchDashboardStats();
    }
  }, [activeTab]);

  // Fetch all users from backend
  useEffect(() => {
    const fetchUsers = async () => {
      try {
  const response = await fetch(`${API}/users`);
        
        // Check if response is JSON
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Server returned non-JSON response");
        }
        
        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error("Error fetching users:", error);
        setError("Failed to load users. Please check if the server is running.");
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (currentProduct?.image) {
  setImagePreview(`${API_BASE}${currentProduct.image}`);
    } else {
      setImagePreview(null);
    }
  }, [currentProduct]);

  // Update state when editing an existing zone
  useEffect(() => {
    if (currentZone) {
      setZoneFormData({
        name: currentZone.name || '',
        areas: currentZone.areas?.join(', ') || '',
        deliveryFee: currentZone.deliveryFee || 1500,
        minOrderAmount: currentZone.minOrderAmount || 0,
        estimatedDeliveryTime: currentZone.estimatedDeliveryTime || '30-45 minutes',
        isActive: currentZone.isActive !== false
      });
    } else {
      setZoneFormData({
        name: '',
        areas: '',
        deliveryFee: 1500,
        minOrderAmount: 0,
        estimatedDeliveryTime: '30-45 minutes',
        isActive: true
      });
    }
  }, [currentZone, showZoneModal]);

  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
  const res = await axios.get(`${API}/users`);
      setCustomers(res.data);
    } catch (error) {
      setError("Error fetching customers");
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (user) => {
    setEditingUser(user._id);
    setEditData({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      role: user.role || "customer"
    });
  };

  const saveEdit = async (userId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update user');
      }
      
      const updatedUser = await response.json();
      
      // Update local state
      setUsers(users.map(user => 
        user._id === userId ? {...user, ...updatedUser} : user
      ));

      setCustomers(customers.map(customer => 
        customer._id === userId ? {...customer, ...updatedUser} : customer
      ));
      
      setSuccessMessage('User updated successfully!');
      setEditingUser(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteUser = (userId) => {
    handleDeleteCustomer(userId);
  };

  // Call this in useEffect
  useEffect(() => {
    if (activeTab === 'customers') {
      fetchCustomers();
    }
  }, [activeTab]);

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/users/${customerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete customer');
      
      setUsers(users.filter(u => u._id !== customerId));
      setCustomers(customers.filter(c => c._id !== customerId));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter users by search or role
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter ? user.role === roleFilter : true;

    return matchesSearch && matchesRole;
  });

  // Add this useEffect to check authentication
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login as admin first');
        navigate('/admin/login');
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Fetch products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
  const response = await fetch(`${API}/products`);
        if (!response.ok) throw new Error('Failed to fetch products');
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Handle product save (create/update)
  const handleSaveProduct = async (formData) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

  let url = `${API}/products`;
      let method = 'POST';
      
      if (currentProduct) {
  url = `${API}/products/${currentProduct._id}`;
        method = 'PUT';
        
        // For updates, we need to handle both FormData and JSON
        if (formData.get('image') && formData.get('image').name) {
          // If there's a new image, use FormData
          const response = await fetch(url, {
            method: method,
            headers: { 
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
          
          if (!response.ok) throw new Error('Failed to update product');
          // Update state...
        } else {
          // If no new image, use JSON
          const jsonData = {
            name: formData.get('name'),
            category: formData.get('category'),
            price: parseFloat(formData.get('price')),
            stock: parseInt(formData.get('stock')),
            featured: formData.get('featured') === 'on',
            rating: parseFloat(formData.get('rating'))
          };
          
          const response = await fetch(url, {
            method: method,
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(jsonData)
          });
          
          if (!response.ok) throw new Error('Failed to update product');
          // Update state...
        }
      } else {
        // Create new product
        const response = await fetch(url, {
          method: method,
          headers: { 
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (!response.ok) throw new Error('Failed to create product');
        // Update state...
      }

      // Refresh products after operation
  const productsResponse = await fetch(`${API}/products`);
      const productsData = await productsResponse.json();
      setProducts(productsData);

      setShowProductModal(false);
      setCurrentProduct(null);
      setSuccessMessage(currentProduct ? 'Product updated successfully!' : 'Product added successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle product deletion
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete product');

      // Remove product from local state
      setProducts(products.filter(p => p._id !== productId));
      setSuccessMessage('Product deleted successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fetch orders from backend
  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch orders');
      
      const ordersData = await response.json();
      setOrders(ordersData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Update order status
  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update order status');
      
      // Update local state
      setOrders(orders.map(order => 
        order._id === orderId ? { ...order, status: newStatus } : order
      ));
      
      setSuccessMessage('Order status updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Update payment status
  const handlePaymentStatusUpdate = async (orderId, newPaymentStatus) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ paymentStatus: newPaymentStatus })
      });

      if (!response.ok) {throw new Error('Failed to update payment status');}
      
      // Update local state
      setOrders(orders.map(order => 
        order._id === orderId ? { ...order, paymentStatus: newPaymentStatus } : order
      ));
      
      setSuccessMessage('Payment status updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete order
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) return;
    
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/orders/${orderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete order');
      
      // Remove from local state
      setOrders(orders.filter(order => order._id !== orderId));
      setSuccessMessage('Order deleted successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // View order details
  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  // Filter orders based on status and date
  const filteredOrders = orders.filter(order => {
    const matchesStatus = orderStatusFilter ? order.status === orderStatusFilter : true;
    const matchesPaymentStatus = paymentStatusFilter ? order.paymentStatus === paymentStatusFilter : true;
    const matchesDate = orderDateFilter ? 
      new Date(order.createdAt).toLocaleDateString() === new Date(orderDateFilter).toLocaleDateString() : 
      true;
    
    return matchesStatus && matchesPaymentStatus && matchesDate;
  });

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders();
    }
  }, [activeTab]);

  // Fetch support requests
  const fetchSupportRequests = async () => {
    try {
      setSupportRequestsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/support-requests?status=${requestStatusFilter}&category=${requestCategoryFilter}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch support requests');
      
      const data = await response.json();
      setSupportRequests(data.requests);
    } catch (err) {
      setError(err.message);
    } finally {
      setSupportRequestsLoading(false);
    }
  };

  // Fetch support requests when the tab is active
  useEffect(() => {
    const fetchSupportRequests = async () => {
      try {
        setSupportRequestsLoading(true);
        const token = localStorage.getItem('token');
  const response = await fetch(`${API}/support-requests?status=${requestStatusFilter}&category=${requestCategoryFilter}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) throw new Error('Failed to fetch support requests');
        
        const data = await response.json();
        setSupportRequests(data.requests);
      } catch (err) {
        setError(err.message);
      } finally {
        setSupportRequestsLoading(false);
      }
    };

    if (activeTab === 'support-requests') {
      fetchSupportRequests();
    }
  }, [activeTab, requestStatusFilter, requestCategoryFilter]);

  // Update a support request
  const handleRequestUpdate = async (requestId, updates) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/support-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update support request');
      }

      const updatedRequest = await response.json();

      // Update local state
      setSupportRequests(supportRequests.map(req => 
        req._id === requestId ? {...req, ...updatedRequest.request} : req
      ));
      
      // Refresh support requests
      fetchSupportRequests();
      setSuccessMessage('Support request updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Add a response to a support request
  const handleAddResponse = async (requestId) => {
    try {
      if (!responseText.trim()) {
        setError('Response message cannot be empty');
        return;
      }
      
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/support-requests/${requestId}/response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: responseText })
      });

      if (!response.ok) throw new Error('Failed to add response');
      
      // Refresh the selected request
      const updatedRequest = await response.json();
      setSelectedRequest(updatedRequest.request);
      setResponseText('');
      setSuccessMessage('Response added successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // View support request details
  const viewRequestDetails = async (request) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/support-requests/${request._id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch support request details');
      
      const requestDetails = await response.json();
      setSelectedRequest(requestDetails);
      setShowRequestModal(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('token');
    navigate('/admin-login');
  };

  const sidebarStyle = {
    card: {
      height: 'fit-content',
      minHeight: 'auto'
    },
    navLink: {
      borderRadius: '0.375rem',
      marginBottom: '0.25rem',
      padding: '0.75rem 1rem',
      transition: 'all 0.2s ease',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  };

  const handleStoreSettingsSave = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');

  const response = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ store: settings.store })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save store settings');
      }
      
      const updatedSettings = await response.json();
      setSettings(updatedSettings);
      setSuccessMessage('Store settings saved successfully!');
    } catch (err) {
      setError(err.message || 'Failed to save store settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationSettingsSave = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ notifications: settings.notifications })
      });

      if (!response.ok) throw new Error('Failed to save notification settings');
      
      const updatedSettings = await response.json();
      setSettings(updatedSettings);
      setSuccessMessage('Notification settings saved successfully!');
    } catch (err) {
      setError('Failed to save notification settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecuritySettingsSave = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ security: settings.security })
      });

      if (!response.ok) throw new Error('Failed to save security settings');
      
      const updatedSettings = await response.json();
      setSettings(updatedSettings);
      setSuccessMessage('Security settings saved successfully!');
    } catch (err) {
      setError('Failed to save security settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSettingsSave = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
  const response = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ payments: settings.payments })
      });

      if (!response.ok) throw new Error('Failed to save payment settings');
      
      const updatedSettings = await response.json();
      setSettings(updatedSettings);
      setSuccessMessage('Payment settings saved successfully!');
    } catch (err) {
      setError('Failed to save payment settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveZone = async () => {
    try {
      // Validate areas
      const areasArray = zoneFormData.areas.split(',')
        .map(area => area.trim())
        .filter(area => area);
      
      if (areasArray.length === 0) {
        setError('Please add at least one delivery area');
        return;
      }

      setIsLoading(true);
      const token = localStorage.getItem('token');

      // Get form data from the modal
      const formData = {
        name: zoneFormData.name,
        areas: zoneFormData.areas.split(',').map(area => area.trim()).filter(area => area),
        deliveryFee: parseInt(zoneFormData.deliveryFee) || 1500,
        minOrderAmount: parseInt(zoneFormData.minOrderAmount) || 0,
        estimatedDeliveryTime: zoneFormData.estimatedDeliveryTime || '30-45 minutes',
        isActive: zoneFormData.isActive
      };

      // If editing, include the _id
      if (currentZone && currentZone._id) {
        formData._id = currentZone._id;
      } else {
        // Generate a new ID if creating
        formData._id = Date.now().toString();
      }
      
      // Get current settings and update delivery zones
      const updatedZones = settings.store?.deliveryZones ? [...settings.store.deliveryZones] : [];
      
      if (currentZone && currentZone._id) {
        // Update existing zone
        const index = updatedZones.findIndex(z => z._id === currentZone._id);
        if (index !== -1) {
          updatedZones[index] = formData;
        } else {
          updatedZones.push(formData);
        }
      } else {
        // Add new zone
        updatedZones.push(formData);
      }
      
  const response = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          store: {
            ...settings.store,
            deliveryZones: updatedZones
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save delivery zones');
      }
      
      const updatedSettings = await response.json();
      setSettings(updatedSettings);
      setShowZoneModal(false);
      setCurrentZone(null);
      setSuccessMessage('Delivery zone saved successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm('Are you sure you want to delete this delivery zone?')) return;
    
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      // Filter out the zone to delete
      const updatedZones = settings.deliveryZones.filter(z => z._id !== zoneId);
      
  const response = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          store: {
            ...settings.store,
            deliveryZones: updatedZones
          }
        })
      });

      if (!response.ok) throw new Error('Failed to delete delivery zone');
      
      // Update local state with the new zones array
      setSettings({
        ...settings,
        store: {
          ...settings.store,
          deliveryZones: updatedZones
        }
      });
      
      setSuccessMessage('Delivery zone deleted successfully!');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'delivered':
        return <Badge bg="success"><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Delivered</Badge>;
      case 'pending':
        return <Badge bg="warning"> Pending</Badge>;
      case 'confirmed':
        return <Badge bg="secondary"><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Confirmed</Badge>
      case 'preparing':
        return <Badge bg="primary"><FontAwesomeIcon icon={faUtensils} className="me-1" /> Preparing</Badge>
      case 'cancelled':
        return <Badge bg="danger"><FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Cancelled</Badge>;
      case 'out-for-delivery':
        return <Badge bg="info"><FontAwesomeIcon icon={faTruck} className="me-1" /> Out for delivery</Badge>;
      default:
        return <Badge bg="secondary"> {status}</Badge>;
    }
  };

  const getPaymentStatusBadge = (status) => {
    switch(status) {
      case 'paid':
        return <Badge bg="success">Paid</Badge>;
      case 'pending':
        return <Badge bg="secondary">Pending</Badge>;
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      case 'refunded':
        return <Badge bg="warning">Refunded</Badge>;
      default:
        return <Badge bg="secondary">{status || 'pending'}</Badge>;
    }
  };

  return (
    <div className="admin-dashboard">
      <Container fluid className="px-4">
        {isLoading && (
          <div className="text-center py-3">
            <div className="spinner-border text-danger" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* Success and Error Alerts */}
        <div style={{ position: 'relative', zIndex: 1050 }}>
          {successMessage && (
            <Alert 
              variant="success" 
              onClose={() => setSuccessMessage('')} 
              dismissible
              style={{ position: 'fixed', top: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 1050, minWidth: '300px' }}
            >
              {successMessage}
            </Alert>
          )}
          {error && (
            <Alert 
              variant="danger" 
              onClose={() => setError(null)} 
              dismissible
              style={{ position: 'fixed', top: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 1050, minWidth: '300px' }}
            >
              {error}
            </Alert>
          )}
        </div>
          
        <Row className="g-4 my-2">
          <Col lg={3}>
            {/* Admin Sidebar */}
            <Card className="border-0 shadow-sm" style={sidebarStyle.card}>
              <Card.Body className="p-0">
                <div className="p-3 bg-danger text-white">
                  <h5 className="mb-0">Admin Dashboard</h5>
                </div>
                <Nav variant="pills" className="flex-column p-3">
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'dashboard'} 
                      onClick={() => setActiveTab('dashboard')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faChartLine} className="me-2" />
                      Overview
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'orders'} 
                      onClick={() => setActiveTab('orders')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faList} className="me-2" />
                      Orders
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'products'} 
                      onClick={() => setActiveTab('products')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faBox} className="me-2" />
                      Products
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'customers'} 
                      onClick={() => setActiveTab('customers')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faUsers} className="me-2" />
                      Customers
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'support-requests'} 
                      onClick={() => setActiveTab('support-requests')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faLifeRing} className="me-2" />
                      Support Requests
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'settings'} 
                      onClick={() => setActiveTab('settings')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faCog} className="me-2" />
                      Settings
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      onClick={handleAdminLogout}
                      className="d-flex align-items-center text-danger"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                      Logout
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={9}>
            {/* Main Content Area */}
            <Tab.Content>
              {/* Dashboard Overview */}
              <Tab.Pane active={activeTab === 'dashboard'}>
                <Row className="g-4">
                  <Col md={4}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <h6 className="text-muted">Total Orders</h6>
                        <h3 className="fw-bold">{dashboardStats.totalOrders}</h3>
                        <small className="text-muted">All time orders</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <h6 className="text-muted">Revenue</h6>
                        <h3 className="fw-bold">₦{dashboardStats.revenue.toLocaleString()}</h3>
                        <small className="text-muted">Total revenue</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <h6 className="text-muted">Active Products</h6>
                        <h3 className="fw-bold">{dashboardStats.activeProducts}</h3>
                        <small className="text-muted">Available products</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Recent Orders */}
                <Card className="border-0 shadow-sm mt-4">
                  <Card.Body>
                    <h5 className="mb-3">Recent Orders</h5>
                    {dashboardStats.recentOrders.length > 0 ? (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Order ID</th>
                              <th>Customer</th>
                              <th>Date</th>
                              <th>Amount</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dashboardStats.recentOrders.map(order => (
                              <tr key={order._id}>
                                <td data-label="Order ID" className="fw-semibold text-start">...{order._id.slice(-10)}</td>
                                <td data-label="Customer" className="text-start">
                                  {order.customerName || order.customer?.name || 'Guest'}
                                </td>
                                <td data-label="Date" className="text-start">
                                  {new Date(order.createdAt).toLocaleDateString()}
                                </td>
                                <td data-label="Amount" className="text-start">
                                  ₦{order.total?.toLocaleString() || '0'}
                                </td>
                                <td data-label="Status" className="text-start text-nowrap">
                                  {getStatusBadge(order.status || 'pending')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <p className="text-center text-muted py-4">No recent orders</p>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Orders Management */}
              <Tab.Pane active={activeTab === 'orders'}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-start mb-4 gap-3">
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faList} className="me-2 text-danger" />
                        Order Management
                      </h4>

                      {/* Filters */}
                      <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                          <Row>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label>Order Status</Form.Label>
                                <Form.Select 
                                  value={orderStatusFilter} 
                                  onChange={(e) => setOrderStatusFilter(e.target.value)}
                                >
                                  <option value="">All Statuses</option>
                                  <option value="pending">Pending</option>
                                  <option value="confirmed">Confirmed</option>
                                  <option value="preparing">Preparing</option>
                                  <option value="out-for-delivery">Out for delivery</option>
                                  <option value="delivered">Delivered</option>
                                  <option value="cancelled">Cancelled</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label>Payment Status</Form.Label>
                                <Form.Select 
                                  value={paymentStatusFilter} 
                                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                                >
                                  <option value="">All Payments</option>
                                  <option value="pending">Pending</option>
                                  <option value="paid">Paid</option>
                                  <option value="failed">Failed</option>
                                  <option value="refunded">Refunded</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label>Order Date</Form.Label>
                                <Form.Control 
                                  type="date" 
                                  value={orderDateFilter}
                                  onChange={(e) => setOrderDateFilter(e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={3} className="d-flex align-items-end">
                              <Button 
                                variant="outline-secondary" 
                                className="m-2 "
                                onClick={() => {
                                  setOrderStatusFilter('');
                                  setPaymentStatusFilter('');
                                  setOrderDateFilter('');
                                }}
                              >
                                Clear Filters
                              </Button>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </div>

                    {isLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-danger" role="status">
                          <span className="visually-hidden">Loading orders...</span>
                        </div>
                      </div>
                    ) : error ? (
                      <Alert variant="danger" className="text-center">
                        {error}
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Order ID</th>
                              <th>Customer</th>
                              <th>Phone</th>
                              <th>Date</th>
                              <th>Items</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Payment</th>
                              <th>Payment Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredOrders.map(order => (
                              <tr key={order._id}>
                                <td className="fw-semibold text-start">
                                  <small>...{order._id.slice(-8)}</small>
                                </td>
                                <td className="text-start">
                                  <div>
                                    <strong>{order.customerName || order.customer?.name || 'Guest'}</strong>
                                    {order.email && <div className="text-muted small">{order.email}</div>}
                                  </div>
                                </td>
                                <td className="text-start">
                                  {order.phone || order.customer?.phone || 'N/A'}
                                </td>
                                <td className="text-start">
                                  {new Date(order.createdAt).toLocaleDateString()}
                                  <div className="text-muted small">
                                    {new Date(order.createdAt).toLocaleTimeString()}
                                  </div>
                                </td>
                                <td className="text-start">
                                  {order.items?.length || 0} items
                                  <div className="text-muted small">
                                    {order.items?.slice(0, 2).map(item => item.name).join(', ')}
                                    {order.items?.length > 2 && '...'}
                                  </div>
                                </td>
                                <td className="text-start fw-bold">
                                  ₦{order.total?.toLocaleString() || '0'}
                                </td>
                                <td className="text-start">
                                  <Form.Select
                                    size="sm"
                                    value={order.status || 'pending'}
                                    onChange={(e) => handleStatusUpdate(order._id, e.target.value)}
                                    style={{ width: '120px' }}
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="preparing">Preparing</option>
                                    <option value="out-for-delivery">Out for delivery</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="cancelled">Cancelled</option>
                                  </Form.Select>
                                </td>
                                <td className="text-start">
                                  <Badge bg={
                                    order.paymentMethod === 'card' ? 'primary' :
                                    order.paymentMethod === 'transfer' ? 'info' :
                                    order.paymentMethod === 'cash_on_delivery' ? 'warning' : 'secondary'
                                  }>
                                    {order.paymentMethod === 'card' ? 'Card' :
                                    order.paymentMethod === 'transfer' ? 'Transfer' :
                                    order.paymentMethod === 'cash_on_delivery' ? 'Cash' : order.paymentMethod}
                                  </Badge>
                                </td>
                                <td className="text-start">
                                  {editingPaymentStatus === order._id ? (
                                    <Form.Select
                                      size="sm"
                                      value={order.paymentStatus || 'pending'}
                                      onChange={(e) => {
                                        handlePaymentStatusUpdate(order._id, e.target.value);
                                        setEditingPaymentStatus(null);
                                      }}
                                      autoFocus
                                      onBlur={() => setEditingPaymentStatus(null)}
                                      style={{ width: '120px' }}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="paid">Paid</option>
                                      <option value="failed">Failed</option>
                                      <option value="refunded">Refunded</option>
                                    </Form.Select>
                                  ) : (
                                    <div 
                                      onClick={() => setEditingPaymentStatus(order._id)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      {getPaymentStatusBadge(order.paymentStatus)}
                                    </div>
                                  )}
                                </td>
                                <td className="text-start">
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => viewOrderDetails(order)}
                                    title="View Details"
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteOrder(order._id)}
                                    title="Delete Order"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {filteredOrders.length === 0 && !isLoading && (
                      <div className="text-center py-5 text-muted">
                        <FontAwesomeIcon icon={faList} size="3x" className="mb-3" />
                        <h5>No orders found</h5>
                        <p>Try adjusting your filters or check back later</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Products Management */}
              <Tab.Pane active={activeTab === 'products'}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faBox} className="me-2 text-danger" />
                        Product Management
                      </h4>
                      <Button 
                        variant="danger" 
                        onClick={() => {
                          setCurrentProduct(null);
                          setShowProductModal(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        Add Product
                      </Button>
                    </div>

                    {/* Search Bar */}
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Control
                                type="text"
                                placeholder="Search products by name or category..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    {filteredProducts.length > 0 ? (
                      <div className="table-responsive">
                        <Table hover className="align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Image</th>
                              <th>Product Name</th>
                              <th>Category</th>
                              <th>Price</th>
                              <th>Stock</th>
                              <th>Featured</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredProducts.map(product => (
                              <tr key={product._id}>
                                <td>
                                  <img 
                                    src={product.image ? getImageUrl(product.image) : 'https://via.placeholder.com/300x180?text=No+Image'} 
                                    alt={product.name}
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/300x180?text=No+Image';
                                    }}
                                  />
                                </td>
                                <td data-label="Product Name" className="text-start">{product.name}</td>
                                <td data-label="Category" className="text-start">{product.category}</td>
                                <td data-label="Price" className="text-start">₦{product.price?.toLocaleString()}</td>
                                <td data-label="Stock" className="text-start">{product.stock}</td>
                                <td data-label="Featured" className="text-start">
                                  {product.featured ? (
                                    <Badge bg="success">Yes</Badge>
                                  ) : (
                                    <Badge bg="secondary">No</Badge>
                                  )}
                                </td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => {
                                      setCurrentProduct(product);
                                      setShowProductModal(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteProduct(product._id)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <FaBox size={40} className="mx-auto mb-3 text-secondary" />
                        <h5 className="mb-2">No Products Found</h5>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Customers Management */}
              <Tab.Pane active={activeTab === 'customers'}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faUsers} className="me-2 text-danger" />
                        Customer Management
                      </h4>
                    </div>

                    {/* Filters */}
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Control
                                type="text"
                                placeholder="Search customers by name, email, or phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                              >
                                <option value="">All Roles</option>
                                <option value="customer">Customer</option>
                                <option value="admin">Admin</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3} className="d-flex align-items-end">
                            <Button 
                              variant="outline-secondary" 
                              className="m-2"
                              onClick={() => {
                                setSearchTerm('');
                                setRoleFilter('');
                              }}
                            >
                              Clear Filters
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    {/* Table */}
                    {filteredUsers.length > 0 ? (
                      <div className="table-responsive">
                        <Table hover className="align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Role</th>
                              <th>Joined</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredUsers.map((user) => (
                              <tr key={user._id}>
                                {editingUser === user._id ? (
                                  <>
                                    <td>
                                      <Form.Control
                                        type="text"
                                        value={editData.name}
                                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                        size="sm"
                                      />
                                    </td>
                                    <td>
                                      <Form.Control
                                        type="email"
                                        value={editData.email}
                                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                                        size="sm"
                                      />
                                    </td>
                                    <td>
                                      <Form.Control
                                        type="text"
                                        value={editData.phone}
                                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                                        size="sm"
                                      />
                                    </td>
                                    <td>
                                      <Form.Select
                                        value={editData.role}
                                        onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                                        size="sm"
                                      >
                                        <option value="customer">Customer</option>
                                        <option value="admin">Admin</option>
                                      </Form.Select>
                                    </td>
                                    <td>
                                      {new Date(user.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="d-flex gap-2">
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => saveEdit(user._id)}
                                        disabled={isLoading}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setEditingUser(null)}
                                      >
                                        Cancel
                                      </Button>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td>{user.name || "N/A"}</td>
                                    <td>{user.email}</td>
                                    <td>{user.phone || "—"}</td>
                                    <td>
                                      <Badge bg={user.role === "admin" ? "danger" : "primary"}>
                                        {user.role || "customer"}
                                      </Badge>
                                    </td>
                                    <td>
                                      {new Date(user.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="d-flex gap-2">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => startEdit(user)}
                                        className="d-flex align-items-center gap-1"
                                      >
                                        <FaEdit /> Edit
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => deleteUser(user._id)}
                                        className="d-flex align-items-center gap-1"
                                      >
                                        <FaTrash /> Delete
                                      </Button>
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-5 text-muted">
                        <FaUsers size={40} className="mx-auto mb-2" />
                        <p>No customers found</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Support Requests Management */}
              <Tab.Pane active={activeTab === 'support-requests'}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faLifeRing} className="me-2 text-danger" />
                        Support Request Management
                      </h4>
                    </div>

                    {/* Filters */}
                    <Card className="border-0 shadow-sm mb-4">
                      <Card.Body>
                        <Row>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Status</Form.Label>
                              <Form.Select 
                                value={requestStatusFilter} 
                                onChange={(e) => setRequestStatusFilter(e.target.value)}
                              >
                                <option value="">All Statuses</option>
                                <option value="new">New</option>
                                <option value="in-progress">In Progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Category</Form.Label>
                              <Form.Select 
                                value={requestCategoryFilter} 
                                onChange={(e) => setRequestCategoryFilter(e.target.value)}
                              >
                                <option value="">All Categories</option>
                                <option value="general">General</option>
                                <option value="order">Order</option>
                                <option value="delivery">Delivery</option>
                                <option value="payment">Payment</option>
                                <option value="quality">Quality</option>
                                <option value="suggestion">Suggestion</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4} className="d-flex align-items-end">
                            <Button 
                              variant="outline-secondary" 
                              className="m-2"
                              onClick={() => {
                                setRequestStatusFilter('');
                                setRequestCategoryFilter('');
                              }}
                            >
                              Clear Filters
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    {supportRequestsLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-danger" role="status">
                          <span className="visually-hidden">Loading support requests...</span>
                        </div>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Ticket ID</th>
                              <th>Customer</th>
                              <th>Subject</th>
                              <th>Category</th>
                              <th>Date</th>
                              <th>Status</th>
                              <th>Priority</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {supportRequests.map(request => (
                              <tr key={request._id}>
                                <td className="fw-semibold text-start">
                                  <small>#{request._id.slice(-8).toUpperCase()}</small>
                                </td>
                                <td className="text-start">
                                  {request.customer?.name || 'Unknown Customer'}
                                  <div className="text-muted small">{request.customer?.email}</div>
                                </td>
                                <td className="text-start">{request.subject}</td>
                                <td className="text-start">
                                  <Badge bg="secondary" className="text-capitalize">
                                    {request.category}
                                  </Badge>
                                </td>
                                <td className="text-start">
                                  {new Date(request.createdAt).toLocaleDateString()}
                                </td>
                                <td className="text-start">
                                  <Form.Select
                                    size="sm"
                                    value={request.status}
                                    onChange={(e) => handleRequestUpdate(request._id, { status: e.target.value })}
                                    style={{ width: '140px' }}
                                  >
                                    <option value="new">New</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="resolved">Resolved</option>
                                    <option value="closed">Closed</option>
                                  </Form.Select>
                                </td>
                                <td className="text-start">
                                  <Form.Select
                                    size="sm"
                                    value={request.priority}
                                    onChange={(e) => handleRequestUpdate(request._id, { priority: e.target.value })}
                                    style={{ width: '120px' }}
                                  >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                  </Form.Select>
                                </td>
                                <td className="text-start">
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => viewRequestDetails(request)}
                                    title="View Details"
                                  >
                                    <FontAwesomeIcon icon={faEye} /> Details
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {supportRequests.length === 0 && !supportRequestsLoading && (
                      <div className="text-center py-5 text-muted">
                        <FontAwesomeIcon icon={faLifeRing} size="3x" className="mb-3" />
                        <h5>No support requests found</h5>
                        <p>Try adjusting your filters or check back later</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Settings */}
              <Tab.Pane active={activeTab === 'settings'}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h4 className="mb-4">
                      <FontAwesomeIcon icon={faCog} className="me-2 text-danger" />
                      System Settings
                    </h4>

                    {settingsLoading && (
                      <div className="text-center py-3">
                        <div className="spinner-border text-danger" role="status">
                          <span className="visually-hidden">Loading settings...</span>
                        </div>
                      </div>
                    )}
                    
                    <Tabs defaultActiveKey="store" className="mb-3">
                      {/* Store Settings */}
                      <Tab eventKey="store" title={
                        <span><FontAwesomeIcon icon={faStore} className="me-1" /> Store</span>
                      }>
                        <Card className="border-0 shadow-sm">
                          <Card.Header className="bg-white">
                            <h5 className="mb-0">Store Information</h5>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Store Name</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={settings.store.storeName}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, storeName: e.target.value}
                                    })}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Store Email</Form.Label>
                                  <Form.Control
                                    type="email"
                                    value={settings.store.storeEmail}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, storeEmail: e.target.value}
                                    })}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>

                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Store Phone</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={settings.store.storePhone}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, storePhone: e.target.value}
                                    })}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Currency</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={settings.store.currency}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, currency: e.target.value}
                                    })}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>

                            <Form.Group className="mb-3">
                              <Form.Label>Store Address</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={settings.store.storeAddress}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  store: {...settings.store, storeAddress: e.target.value}
                                })}
                              />
                            </Form.Group>

                            <Row>
                              <Col md={4}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Tax Rate (%)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="30"
                                    value={settings.store?.taxRate || 0}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, taxRate: parseFloat(e.target.value) || 0}
                                    })}
                                  />
                                  <Form.Text className="text-muted">
                                    Set to 0 for no tax
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Delivery Fee (₦)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="500"
                                    min="0"
                                    value={settings.store?.deliveryFee || 1500}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, deliveryFee: parseInt(e.target.value) || 0}
                                    })}
                                  />
                                  <Form.Text className="text-muted">
                                    Standard delivery charge
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Free Delivery Threshold (₦)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="1000"
                                    min="0"
                                    value={settings.store?.freeDeliveryThreshold || 10000}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, freeDeliveryThreshold: parseInt(e.target.value) || 0}
                                    })}
                                  />
                                  <Form.Text className="text-muted">
                                    Orders above this amount get free delivery
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Minimum Order (₦)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={settings.store.minOrderAmount}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, minOrderAmount: parseInt(e.target.value)}
                                    })}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                              
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Opening Time</Form.Label>
                                  <Form.Control
                                    type="time"
                                    value={settings.store.openingTime}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, openingTime: e.target.value}
                                    })}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Closing Time</Form.Label>
                                  <Form.Control
                                    type="time"
                                    value={settings.store.closingTime}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      store: {...settings.store, closingTime: e.target.value}
                                    })}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>

                            <div className="text-end">
                              <Button 
                                variant="danger" 
                                onClick={handleStoreSettingsSave}
                                disabled={isLoading}
                              >
                                {isLoading ? 'Saving...' : 'Save Store Settings'}
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Tab>

                      {/* Delivery zone management */}
                      <Tab eventKey="delivery-zones" title={
                        <span><FontAwesomeIcon icon={faTruck} className="me-1" /> Delivery Zones</span>
                      }>
                        <Card className="border-0 shadow-sm">
                          <Card.Header className="bg-white">
                            <div className="d-flex justify-content-between align-items-center">
                              <h5 className="mb-0">Delivery Zone Management</h5>
                              <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => {
                                  setCurrentZone({
                                    name: '',
                                    areas: [''],
                                    deliveryFee: 1500,
                                    minOrderAmount: 0,
                                    estimatedDeliveryTime: '30-45 minutes',
                                    isActive: true
                                  });
                                  setShowZoneModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faPlus} className="me-1" />
                                Add Zone
                              </Button>
                            </div>
                          </Card.Header>
                          <Card.Body>
                            {settings.store?.deliveryZones?.length > 0 ? (
                              <div className="table-responsive">
                                <Table hover className="align-middle">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Zone Name</th>
                                      <th>Areas Covered</th>
                                      <th>Delivery Fee</th>
                                      <th>Min Order</th>
                                      <th>Status</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {settings.store.deliveryZones.map((zone, index) => (
                                      <tr key={zone._id || index}>
                                        <td>{zone.name}</td>
                                        <td>
                                          <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                                            {Array.isArray(zone.areas) ? zone.areas.join(', ') : typeof zone.areas === 'string' ? zone.areas : 'No areas specified'}
                                          </div>
                                        </td>
                                        <td>₦{zone.deliveryFee?.toLocaleString() || '0'}</td>
                                        <td>₦{zone.minOrderAmount?.toLocaleString() || '0'}</td>
                                        <td>
                                          <Badge bg={zone.isActive ? 'success' : 'secondary'}>
                                            {zone.isActive ? 'Active' : 'Inactive'}
                                          </Badge>
                                        </td>
                                        <td>
                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => {
                                              setCurrentZone(zone);
                                              setShowZoneModal(true);
                                            }}
                                          >
                                            <FontAwesomeIcon icon={faEdit} />
                                          </Button>
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteZone(zone._id)}
                                          >
                                            <FontAwesomeIcon icon={faTrash} />
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-center py-5 text-muted">
                                <FontAwesomeIcon icon={faTruck} size="3x" className="mb-3" />
                                <h5>No delivery zones configured</h5>
                                <p>Add delivery zones to set different fees for different areas</p>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </Tab>

                      {/* Notification Settings */}
                      <Tab eventKey="notifications" title={
                        <span><FontAwesomeIcon icon={faBell} className="me-1" /> Notifications</span>
                      }>
                        <Card className="border-0 shadow-sm">
                          <Card.Header className="bg-white">
                            <h5 className="mb-0">Notification Preferences</h5>
                          </Card.Header>
                          <Card.Body>
                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="email-notifications"
                                label="Enable Email Notifications"
                                checked={settings.notifications.emailNotifications}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  notifications: {
                                    ...settings.notifications, 
                                    emailNotifications: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="order-alerts"
                                label="Enable Order Alerts"
                                checked={settings.notifications.orderAlerts}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  notifications: {
                                    ...settings.notifications, 
                                    orderAlerts: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="low-stock-alerts"
                                label="Enable Low Stock Alerts"
                                checked={settings.notifications.lowStockAlerts}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  notifications: {
                                    ...settings.notifications, 
                                    lowStockAlerts: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="new-user-alerts"
                                label="Enable New User Alerts"
                                checked={settings.notifications.newUserAlerts}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  notifications: {
                                    ...settings.notifications, 
                                    newUserAlerts: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="marketing-emails"
                                label="Enable Marketing Emails"
                                checked={settings.notifications.marketingEmails}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  notifications: {
                                    ...settings.notifications, 
                                    marketingEmails: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <div className="text-end">
                              <Button 
                                variant="danger" 
                                onClick={handleNotificationSettingsSave}
                                disabled={isLoading}
                              >
                                {isLoading ? 'Saving...' : 'Save Notification Settings'}
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Tab>

                      {/* Security Settings */}
                      <Tab eventKey="security" title={
                        <span><FontAwesomeIcon icon={faShield} className="me-1" /> Security</span>
                      }>
                        <Card className="border-0 shadow-sm">
                          <Card.Header className="bg-white">
                            <h5 className="mb-0">Security Settings</h5>
                          </Card.Header>
                          <Card.Body>
                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="two-factor-auth"
                                label="Enable Two-Factor Authentication"
                                checked={settings.security.twoFactorAuth}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  security: {
                                    ...settings.security, 
                                    twoFactorAuth: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Session Timeout (minutes)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={settings.security.sessionTimeout}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      security: {
                                        ...settings.security, 
                                        sessionTimeout: parseInt(e.target.value)
                                      }
                                    })}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Password Expiry (days)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={settings.security.passwordExpiry}
                                    onChange={(e) => setSettings({
                                      ...settings, 
                                      security: {
                                        ...settings.security, 
                                        passwordExpiry: parseInt(e.target.value)
                                      }
                                    })}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>

                            <Form.Group className="mb-3">
                              <Form.Label>Maximum Login Attempts</Form.Label>
                              <Form.Control
                                type="number"
                                value={settings.security.loginAttempts}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  security: {
                                    ...settings.security, 
                                    loginAttempts: parseInt(e.target.value)
                                  }
                                })}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>IP Whitelist (comma separated)</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={settings.security.ipWhitelist.join(', ')}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  security: {
                                    ...settings.security, 
                                    ipWhitelist: e.target.value.split(',').map(ip => ip.trim()).filter(ip => ip)
                                  }
                                })}
                                placeholder="192.168.1.1, 192.168.1.2"
                              />
                              <Form.Text className="text-muted">
                                Add IP addresses that are allowed to access the admin panel
                              </Form.Text>
                            </Form.Group>

                            <div className="text-end">
                              <Button 
                                variant="danger" 
                                onClick={handleSecuritySettingsSave}
                                disabled={isLoading}
                              >
                                {isLoading ? 'Saving...' : 'Save Security Settings'}
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Tab>

                      {/* Payment Settings */}
                      <Tab eventKey="payments" title={
                        <span><FontAwesomeIcon icon={faCreditCard} className="me-1" /> Payments</span>
                      }>
                        <Card className="border-0 shadow-sm">
                          <Card.Header className="bg-white">
                            <h5 className="mb-0">Payment Methods</h5>
                          </Card.Header>
                          <Card.Body>
                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="cash-on-delivery"
                                label="Enable Cash on Delivery"
                                checked={settings.payments.cashOnDelivery}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  payments: {
                                    ...settings.payments, 
                                    cashOnDelivery: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="bank-transfer"
                                label="Enable Bank Transfer"
                                checked={settings.payments.bankTransfer}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  payments: {
                                    ...settings.payments, 
                                    bankTransfer: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="card-payment"
                                label="Enable Card Payment"
                                checked={settings.payments.cardPayment}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  payments: {
                                    ...settings.payments, 
                                    cardPayment: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="paystack"
                                label="Enable Paystack Integration"
                                checked={settings.payments.paystack}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  payments: {
                                    ...settings.payments, 
                                    paystack: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Check
                                type="switch"
                                id="flutterwave"
                                label="Enable Flutterwave Integration"
                                checked={settings.payments.flutterwave}
                                onChange={(e) => setSettings({
                                  ...settings, 
                                  payments: {
                                    ...settings.payments, 
                                    flutterwave: e.target.checked
                                  }
                                })}
                              />
                            </Form.Group>

                            <div className="text-end">
                              <Button 
                                variant="danger" 
                                onClick={handlePaymentSettingsSave}
                                disabled={isLoading}
                              >
                                {isLoading ? 'Saving...' : 'Save Payment Settings'}
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Tab>
                    </Tabs>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Container>

      {/* Order Details Modal */}
      <Modal show={showOrderModal} onHide={() => setShowOrderModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Order Details #{selectedOrder?._id?.slice(-8)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <h6>Customer Information</h6>
                  <p className="mb-1"><strong>Name:</strong> {selectedOrder.customerName || 'Guest'}</p>
                  <p className="mb-1"><strong>Email:</strong> {selectedOrder.email || 'N/A'}</p>
                  <p className="mb-1"><strong>Phone:</strong> {selectedOrder.phone || 'N/A'}</p>
                </Col>
                <Col md={6}>
                  <h6>Order Information</h6>
                  <p className="mb-1"><strong>Order ID:</strong> {selectedOrder._id}</p>
                  <p className="mb-1"><strong>Date:</strong> {new Date(selectedOrder.createdAt).toLocaleString()}</p>
                  <p className="mb-1"><strong>Status:</strong> {selectedOrder.status}</p>
                  <p className="mb-1"><strong>Payment:</strong> {selectedOrder.paymentMethod}</p>
                  <p className="mb-1"><strong>Payment Status:</strong> 
                    {getPaymentStatusBadge(selectedOrder.paymentStatus)}
                  </p>
                </Col>
              </Row>

              {selectedOrder.scheduledDelivery && (
                <Row className="mb-3">
                  <Col md={12}>
                    <h6>Scheduled Delivery</h6>
                    <p>
                      <strong>Date:</strong> {selectedOrder.scheduledDelivery.date || 'Not specified'}
                    </p>
                    <p>
                      <strong>Time:</strong> {selectedOrder.scheduledDelivery.time ? (
                        (() => {
                          const [hours, minutes] = selectedOrder.scheduledDelivery.time.split(':');
                          const hourInt = parseInt(hours, 10);
                          const period = hourInt >= 12 ? 'PM' : 'AM';
                          const hour12 = hourInt % 12 || 12; // Convert 0 or 24 to 12
                          return `${hour12}:${minutes || '00'} ${period}`;
                        })()
                      ) : 'Not specified'}
                    </p>
                  </Col>
                </Row>
              )}

              <h6>Shipping Address</h6>
              <p className="mb-3">{selectedOrder.shippingAddress || 'No address provided'}</p>

              <h6>Delivery Notes</h6>
              <p className="mb-3">{selectedOrder.deliveryNotes || 'No delivery notes provided'}</p>

              <h6>Order Items</h6>
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items?.map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>₦{item.price?.toLocaleString()}</td>
                      <td>₦{(item.price * item.quantity)?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="text-end"><strong>Subtotal:</strong></td>
                    <td><strong>₦{selectedOrder.subtotal?.toLocaleString() || '0'}</strong></td>
                  </tr>
                  {selectedOrder.taxRate > 0 && (
                    <tr>
                      <td colSpan="3" className="text-end"><strong>Tax ({selectedOrder.taxRate}%):</strong></td>
                      <td><strong>₦{selectedOrder.taxAmount?.toLocaleString() || '0'}</strong></td>
                    </tr>
                  )}
                  {(selectedOrder.deliveryFee > 0 || selectedOrder.deliveryFee === undefined) && (
                    <tr>
                      <td colSpan="3" className="text-end"><strong>Shipping Fee:</strong></td>
                      <td><strong>₦{selectedOrder.deliveryFee?.toLocaleString() || '0'}</strong></td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan="3" className="text-end"><strong>Total:</strong></td>
                    <td><strong>₦{selectedOrder.total?.toLocaleString() || '0'}</strong></td>
                  </tr>
                </tfoot>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Product Modal */}
      <Modal show={showProductModal} onHide={() => {
        setShowProductModal(false);
        setCurrentProduct(null);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentProduct ? 'Edit Product' : 'Add New Product'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
          <Form id="productForm" onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            handleSaveProduct(formData);
          }}>
            <Form.Group className="mb-3" controlId="productName">
              <Form.Label>Product Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                defaultValue={currentProduct?.name || ''}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="productCategory">
              <Form.Label>Category</Form.Label>
              <Form.Control
                type="text"
                name="category"
                defaultValue={currentProduct?.category || ''}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="productPrice">
              <Form.Label>Price (₦)</Form.Label>
              <Form.Control
                type="number"
                name="price"
                min="0"
                step="100"
                defaultValue={currentProduct?.price || ''}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="productStock">
              <Form.Label>Stock</Form.Label>
              <Form.Control
                type="number"
                name="stock"
                min="1"
                defaultValue={currentProduct?.stock || ''}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="productRating">
              <Form.Label>Rating</Form.Label>
              <Form.Control
                type="number"
                name="rating"
                min="0"
                max="5"
                step="0.1"
                defaultValue={currentProduct?.rating || ''}
                required
                placeholder="e.g. 4.5"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="productImage">
              <Form.Label>Image</Form.Label>
              {imagePreview && (
                <div className="mb-2">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                  />
                </div>
              )}
              <Form.Control
                type="file"
                name="image"
                accept="image/*"
                required={!currentProduct}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      setImagePreview(event.target.result);
                    };
                    reader.readAsDataURL(e.target.files[0]);
                  }
                }}
              />
              {currentProduct?.image && (
                <Form.Text className="text-muted">
                  Current image: {currentProduct.image}
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group className="mb-3" controlId="productFeatured">
              <Form.Check
                type="checkbox"
                name="featured"
                label="Featured Product"
                defaultChecked={currentProduct?.featured || false}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowProductModal(false);
            setCurrentProduct(null);
          }}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            type="submit" 
            form="productForm" 
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : currentProduct ? 'Update Product' : 'Add Product'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Support Requests Modal */}
      <Modal show={showRequestModal} onHide={() => setShowRequestModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Support Request #{selectedRequest?._id?.slice(-8).toUpperCase()}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <h6>Customer Information</h6>
                  <p className="mb-1"><strong>Name:</strong> {selectedRequest.customer?.name || 'Unknown'}</p>
                  <p className="mb-1"><strong>Email:</strong> {selectedRequest.customer?.email || 'N/A'}</p>
                  <p className="mb-1"><strong>Phone:</strong> {selectedRequest.customer?.phone || 'N/A'}</p>
                </Col>
                <Col md={6}>
                  <h6>Request Information</h6>
                  <p className="mb-1"><strong>Subject:</strong> {selectedRequest.subject}</p>
                  <p className="mb-1"><strong>Category:</strong> 
                    <Badge bg="secondary" className="ms-2 text-capitalize">
                      {selectedRequest.category}
                    </Badge>
                  </p>
                  <p className="mb-1"><strong>Status:</strong> 
                    <Badge bg={
                      selectedRequest.status === 'new' ? 'primary' :
                      selectedRequest.status === 'in-progress' ? 'warning' :
                      selectedRequest.status === 'resolved' ? 'success' : 'secondary'
                    } className="ms-2">
                      {selectedRequest.status}
                    </Badge>
                  </p>
                  <p className="mb-1"><strong>Priority:</strong> 
                    <Badge bg={
                      selectedRequest.priority === 'low' ? 'success' :
                      selectedRequest.priority === 'medium' ? 'info' :
                      selectedRequest.priority === 'high' ? 'warning' : 'danger'
                    } className="ms-2">
                      {selectedRequest.priority}
                    </Badge>
                  </p>
                  <p className="mb-1"><strong>Date:</strong> {new Date(selectedRequest.createdAt).toLocaleString()}</p>
                  {selectedRequest.orderId && (
                    <p className="mb-1">
                      <strong>Related Order:</strong> #{selectedRequest.orderId._id?.slice(-8).toUpperCase()}
                    </p>
                  )}
                </Col>
              </Row>

              <h6>Message</h6>
              <div className="border p-3 mb-4 rounded">
                {selectedRequest.message}
              </div>

              <h6>Conversation</h6>
              <div className="border rounded mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {selectedRequest.responses && selectedRequest.responses.length > 0 ? (
                  selectedRequest.responses.map((response, index) => (
                    <div key={index} className={`p-3 ${index % 2 === 0 ? 'bg-light' : ''}`}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>{response.repliedBy?.name || 'Unknown User'}</strong>
                        <small className="text-muted">{new Date(response.createdAt).toLocaleString()}</small>
                      </div>
                      <p className="mb-0">{response.message}</p>
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-center text-muted">
                    No responses yet
                  </div>
                )}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Add Response</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Type your response here..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRequestModal(false)}>
            Close
          </Button>
          <Button 
            variant="danger" 
            onClick={() => handleAddResponse(selectedRequest._id)}
            disabled={isLoading || !responseText.trim()}
          >
            {isLoading ? 'Adding...' : 'Add Response'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delivery Zone Modal */}
      <Modal show={showZoneModal} onHide={() => setShowZoneModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentZone?._id ? 'Edit Delivery Zone' : 'Add Delivery Zone'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form id="zoneForm">
            <Form.Group className="mb-3">
              <Form.Label>Zone Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={zoneFormData.name}
                onChange={(e) => setZoneFormData({...zoneFormData, name: e.target.value})}
                placeholder="Mainland, Island, Outskirts, etc."
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Areas Covered (comma separated)</Form.Label>
              <Form.Control
                as="textarea"
                name="areas"
                rows={3}
                value={zoneFormData.areas}
                onChange={(e) => setZoneFormData({...zoneFormData, areas: e.target.value})}
                placeholder="Victoria Island, Lekki, Ikoyi, etc."
                required
              />
              <Form.Text className="text-muted">
                Enter areas that belong to this delivery zone, separated by commas
              </Form.Text>
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Delivery Fee (₦)</Form.Label>
                  <Form.Control
                    type="number"
                    name="deliveryFee"
                    step="500"
                    value={zoneFormData.deliveryFee}
                    onChange={(e) => setZoneFormData({...zoneFormData, deliveryFee: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Order Amount (₦)</Form.Label>
                  <Form.Control
                    type="number"
                    name="minOrderAmount"
                    step="1000"
                    value={zoneFormData.minOrderAmount}
                    onChange={(e) => setZoneFormData({...zoneFormData, minOrderAmount: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Estimated Delivery Time</Form.Label>
              <Form.Control
                type="text"
                name="estimatedDeliveryTime"
                value={zoneFormData.estimatedDeliveryTime}
                onChange={(e) => setZoneFormData({...zoneFormData, estimatedDeliveryTime: e.target.value})}
                placeholder="e.g., 30-45 minutes"
                required
              />
            </Form.Group>
            
            <Form.Check
              type="switch"
              name="isActive"
              label="Active Zone"
              defaultChecked={zoneFormData.isActive}
              onChange={(e) => setZoneFormData({...zoneFormData, isActive: e.target.checked})}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowZoneModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={() => handleSaveZone()}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Zone'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminDashboard;